import { Link, To, useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { useIsMobile } from '@hooks/use-is-mobile';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { baseTabStyles, TabActiveProps, TabContent, TabDisplay } from './common';

export type RouterTabItem = TabDisplay & {
  to: To;
  section?: string;
  state?: unknown;
  dataCy?: string;
};

export type RouterTabsProps = {
  tabs: RouterTabItem[];
  activeSection?: string;
  hasBorder?: boolean;
  shouldCollapseOnMobile?: boolean;
  /** Accessible name for the tab group. When set, the strip is exposed as an ARIA tablist. */
  ariaLabel?: string;
};

export const RouterTabs = ({
  tabs,
  activeSection,
  hasBorder = false,
  shouldCollapseOnMobile = false,
  ariaLabel,
}: RouterTabsProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const isTablist = ariaLabel !== undefined;

  if (shouldCollapseOnMobile && isMobile) {
    return (
      <Select
        isClearable={false}
        isSearchable={false}
        value={activeSection}
        options={tabs.map((tab) => ({ label: tab.label, value: tab.section ?? '' }))}
        onChange={(section) => {
          const tab = tabs.find((item) => item.section === section);
          if (tab) {
            navigate(tab.to, { state: tab.state });
          }
        }}
      />
    );
  }

  const strip = (
    <Stack orientation="horizontal" role={isTablist ? 'tablist' : undefined} aria-label={ariaLabel}>
      {tabs.map((tab, index) => {
        const isActive = tab.section === activeSection;

        return (
          <TabLink
            key={tab.section ?? index}
            to={tab.to}
            state={tab.state}
            $active={isActive}
            role={isTablist ? 'tab' : undefined}
            aria-selected={isTablist ? isActive : undefined}
            aria-current={isActive ? 'page' : undefined}
            data-cy={tab.dataCy}
            data-testid={tab.dataTestId}
          >
            <TabContent tab={tab} isActive={isActive} />
          </TabLink>
        );
      })}
    </Stack>
  );

  if (!hasBorder) {
    return strip;
  }

  return (
    <Container>
      {strip}
      <Border />
    </Container>
  );
};

const TabLink = styled(Link)<TabActiveProps>(baseTabStyles);

const Container = styled.div({
  position: 'relative',
});

const Border = styled.div({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: pxToRem(1),
  backgroundColor: theme.color.border10,
});
